import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FiDownload, FiCopy, FiCheck, FiMail, FiExternalLink } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import '../assets/scss/style.scss';

import luckyIcon from '../../../assets/lucky_icon.svg';
import appPreview from '../assets/images/marketing-hero-app.png';
import extensionPreview from '../assets/images/marketing-hero-browser-extension.png';

const PageWrap = styled.div`
    background: #fbfbfa;
    color: #171714;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const MainContent = styled.main`
    flex: 1;
    padding-bottom: 96px;
`;

const Container = styled.div`
    max-width: 1080px;
    margin: 0 auto;
    padding: 0 24px;
`;

const HeroSection = styled.section`
    padding: 72px 0 48px;
    text-align: center;

    @media (max-width: 768px) {
        padding: 48px 0 32px;
    }
`;

const EyebrowBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: rgba(43, 136, 217, 0.1);
    color: #2b88d9;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
    font-size: 2.85rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: #171714;
    margin: 0 auto 18px;
    max-width: 820px;

    @media (max-width: 768px) {
        font-size: 2.1rem;
    }
`;

const HeroSubtitle = styled.p`
    font-size: 1.15rem;
    line-height: 1.6;
    color: #706d66;
    max-width: 680px;
    margin: 0 auto 32px;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const HeroActions = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 14px;
`;

const PrimaryButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #2b88d9;
    color: #fff !important;
    font-weight: 600;
    font-size: 0.95rem;
    padding: 12px 24px;
    border-radius: 10px;
    text-decoration: none;
    box-shadow: 0 8px 20px rgba(43, 136, 217, 0.25);
    transition: all 0.2s ease;

    &:hover {
        background: #2577be;
        transform: translateY(-1px);
        box-shadow: 0 10px 24px rgba(43, 136, 217, 0.32);
    }
`;

const SecondaryButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    color: #171714 !important;
    font-weight: 600;
    font-size: 0.95rem;
    padding: 12px 22px;
    border-radius: 10px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
        background: #f7f7f6;
        border-color: rgba(20, 17, 15, 0.2);
    }
`;

const Section = styled.section`
    margin-top: 56px;
`;

const SectionHeader = styled.div`
    margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
    font-size: 1.65rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #171714;
    margin: 0 0 8px;
`;

const SectionDesc = styled.p`
    font-size: 0.96rem;
    color: #706d66;
    margin: 0;
    line-height: 1.5;
`;

const Card = styled.div`
    background: #ffffff;
    border: 1px solid rgba(20, 17, 15, 0.08);
    border-radius: 14px;
    padding: 28px;
    box-shadow: 0 8px 24px rgba(23, 23, 20, 0.03);
`;

const BoilerplateCard = styled(Card)`
    position: relative;
    padding: 32px;
`;

const BoilerplateText = styled.div`
    font-size: 1.04rem;
    line-height: 1.68;
    color: #2b2b28;
    margin: 0 0 24px;

    p {
        margin: 0 0 14px;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const QuickFactsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 16px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid rgba(20, 17, 15, 0.08);
`;

const FactItem = styled.div`
    strong {
        display: block;
        font-size: 0.74rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #706d66;
        margin-bottom: 4px;
    }
    span {
        font-size: 0.95rem;
        font-weight: 600;
        color: #171714;
    }
`;

const CopyButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    font-size: 0.82rem;
    font-weight: 600;
    background: #f4f4f3;
    color: #171714;
    border: 1px solid rgba(20, 17, 15, 0.1);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
        background: #eaeae8;
    }
`;

const LogosGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 22px;
`;

const LogoCard = styled(Card)`
    display: flex;
    flex-direction: column;
    padding: 24px;
`;

const LogoPreviewBox = styled.div`
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: ${({ $dark, $blue }) => ($blue ? '#2b88d9' : $dark ? '#171714' : '#f7f7f6')};
    border: 1px solid ${({ $dark, $blue }) => ($blue ? 'transparent' : $dark ? '#2b2b28' : 'rgba(20, 17, 15, 0.06)')};
    margin-bottom: 20px;
    padding: 24px;

    img {
        max-width: 100%;
        max-height: 70px;
        object-fit: contain;
    }
`;

const LogoInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;

    h3 {
        font-size: 1.05rem;
        font-weight: 700;
        margin: 0;
        color: #171714;
    }

    span {
        font-size: 0.76rem;
        font-weight: 600;
        color: #706d66;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
`;

const DownloadRow = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: auto;
`;

const AssetDownloadLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #171714 !important;
    background: #f4f4f3;
    border: 1px solid rgba(20, 17, 15, 0.08);
    border-radius: 6px;
    text-decoration: none;
    transition: all 0.15s ease;

    &:hover {
        background: #2b88d9;
        color: #fff !important;
        border-color: #2b88d9;
    }
`;

const PaletteGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
`;

const ColorSwatch = styled(Card)`
    padding: 18px;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 24px rgba(23, 23, 20, 0.07);
    }
`;

const SwatchPreview = styled.div`
    height: 72px;
    border-radius: 8px;
    background: ${({ $color }) => $color};
    border: 1px solid ${({ $border }) => $border || 'transparent'};
    margin-bottom: 12px;
`;

const SwatchName = styled.div`
    font-weight: 700;
    font-size: 0.95rem;
    color: #171714;
    margin-bottom: 4px;
`;

const SwatchValue = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.82rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: #706d66;
`;

const MediaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 22px;
`;

const MediaCard = styled(Card)`
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const MediaImageWrap = styled.div`
    background: #ececeb;
    height: 230px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
        transition: transform 0.3s ease;
    }

    &:hover img {
        transform: scale(1.02);
    }
`;

const MediaBody = styled.div`
    padding: 20px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        color: #171714;
    }

    p {
        margin: 3px 0 0;
        font-size: 0.82rem;
        color: #706d66;
    }
`;

const GuidelinesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 18px;
`;

const GuidelineCard = styled(Card)`
    padding: 22px;

    h4 {
        margin: 0 0 10px;
        font-size: 1rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    ul {
        margin: 0;
        padding-left: 18px;
        font-size: 0.88rem;
        line-height: 1.6;
        color: #4b4843;
    }

    li {
        margin-bottom: 6px;
    }
`;

const ContactCard = styled(Card)`
    padding: 36px;
    text-align: center;
    background: #fff;
    border: 1px solid rgba(43, 136, 217, 0.2);
    box-shadow: 0 12px 36px rgba(43, 136, 217, 0.05);

    h3 {
        margin: 0 0 8px;
        font-size: 1.45rem;
        font-weight: 800;
        color: #171714;
    }

    p {
        font-size: 0.98rem;
        color: #706d66;
        max-width: 540px;
        margin: 0 auto 24px;
        line-height: 1.55;
    }
`;

const COLOR_PALETTE = [
    { name: 'Lucky Blue', hex: '#2B88D9', rgb: '43, 136, 217', role: 'Primary Brand' },
    { name: 'Star Gold', hex: '#F2C46C', rgb: '242, 196, 108', role: 'Star Accent / Favorites' },
    { name: 'Starred Highlight', hex: '#FAE6A4', rgb: '250, 230, 164', role: 'Starred Note Surface', border: 'rgba(20, 17, 15, 0.12)' },
    { name: 'Charcoal Dark', hex: '#171714', rgb: '23, 23, 20', role: 'Primary Typography' },
    { name: 'Slate Neutral', hex: '#706D66', rgb: '112, 109, 102', role: 'Secondary Text / Borders' },
    { name: 'Canvas White', hex: '#FBFBFA', rgb: '251, 251, 250', role: 'Page Background', border: 'rgba(20, 17, 15, 0.12)' }
];

const Press = () => {
    const [copiedBoilerplate, setCopiedBoilerplate] = useState(false);
    const [copiedColor, setCopiedColor] = useState(null);

    const boilerplate =
        'Luckynote is an active personal notes and knowledge app that turns saving into an instant reflex and makes captured information work for you. Built around the familiar simplicity of sending a message to yourself, Luckynote lets users capture quick thoughts, voice memos, screenshots, articles, and video links (including Instagram Reels and TikToks) in seconds.\n\nUnlike traditional note apps where saved items sit static in folders, Luckynote automatically interprets what you save: extracting ingredients and cooking steps from recipe videos, dates and locations from events, searchable text from screenshots via automated OCR, and clean summaries from long-form articles. Saved ideas and links instantly turn into actionable tasks, reminders, and structured knowledge without demanding complex manual filing or database setup.';

    const handleCopyBoilerplate = () => {
        navigator.clipboard.writeText(boilerplate);
        setCopiedBoilerplate(true);
        setTimeout(() => setCopiedBoilerplate(false), 2500);
    };

    const handleCopyHex = (hex) => {
        navigator.clipboard.writeText(hex);
        setCopiedColor(hex);
        setTimeout(() => setCopiedColor(null), 2000);
    };

    return (
        <PageWrap>
            <Helmet>
                <title>Press & Media Kit | Luckynote</title>
                <meta
                    name="description"
                    content="Official Luckynote brand assets, logos, product screenshots, brand color palette, and company boilerplate for press, media, and partners."
                />
                <link rel="canonical" href="https://luckynote.io/press" />
                <meta property="og:title" content="Press & Media Kit | Luckynote" />
                <meta
                    property="og:description"
                    content="Download official Luckynote logos, brand assets, and press resources."
                />
                <meta property="og:url" content="https://luckynote.io/press" />
            </Helmet>

            <Header navPosition="right" className="reveal-from-bottom" />

            <MainContent>
                <Container>
                    <HeroSection>
                        <EyebrowBadge>Press & Brand Assets</EyebrowBadge>
                        <HeroTitle>Luckynote Brand Resources & Media Kit</HeroTitle>
                        <HeroSubtitle>
                            Download official logos, product screenshots, brand colors, and company information for reviews, publications, and partner integrations.
                        </HeroSubtitle>
                        <HeroActions>
                            <PrimaryButton href="/brand/luckynote-brand-assets.zip" download>
                                <FiDownload size={18} />
                                Download All Assets (.zip)
                            </PrimaryButton>
                            <SecondaryButton href="#press-contact">
                                <FiMail size={17} />
                                Press Contact
                            </SecondaryButton>
                        </HeroActions>
                    </HeroSection>

                    {/* Section 1: Boilerplate */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>About Luckynote</SectionTitle>
                            <SectionDesc>Official company boilerplate and key facts.</SectionDesc>
                        </SectionHeader>
                        <BoilerplateCard>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#706d66', letterSpacing: '0.04em' }}>
                                    Company Boilerplate
                                </span>
                                <CopyButton onClick={handleCopyBoilerplate}>
                                    {copiedBoilerplate ? <FiCheck size={14} color="#34c759" /> : <FiCopy size={14} />}
                                    {copiedBoilerplate ? 'Copied to clipboard!' : 'Copy Boilerplate'}
                                </CopyButton>
                            </div>
                            <BoilerplateText>
                                <p>
                                    Luckynote is an active personal notes and knowledge app that turns saving into an instant reflex and makes captured information work for you. Built around the familiar simplicity of sending a message to yourself, Luckynote lets users capture quick thoughts, voice memos, screenshots, articles, and video links (including Instagram Reels and TikToks) in seconds.
                                </p>
                                <p>
                                    Unlike traditional note apps where saved items sit static in folders, Luckynote automatically interprets what you save: extracting ingredients and cooking steps from recipe videos, dates and locations from events, searchable text from screenshots via automated OCR, and clean summaries from long-form articles. Saved ideas and links instantly turn into actionable tasks, reminders, and structured knowledge without demanding complex manual filing or database setup.
                                </p>
                            </BoilerplateText>

                            <QuickFactsGrid>
                                <FactItem>
                                    <strong>Founded</strong>
                                    <span>2024</span>
                                </FactItem>
                                <FactItem>
                                    <strong>Founder</strong>
                                    <span>Uros Durdevic</span>
                                </FactItem>
                                <FactItem>
                                    <strong>Core Category</strong>
                                    <span>Active Notes & AI Memory</span>
                                </FactItem>
                                <FactItem>
                                    <strong>Platforms</strong>
                                    <span>iOS, Android, Web, Chrome, Desktop</span>
                                </FactItem>
                                <FactItem>
                                    <strong>Community</strong>
                                    <span>6,000+ Users · 4.5★ Rating</span>
                                </FactItem>
                                <FactItem>
                                    <strong>Pricing</strong>
                                    <span>Free tier · Premium from $5/mo</span>
                                </FactItem>
                            </QuickFactsGrid>
                        </BoilerplateCard>
                    </Section>

                    {/* Section 2: Logos */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Logos & Marks</SectionTitle>
                            <SectionDesc>High-resolution vector SVG and raster PNG files for light and dark backgrounds.</SectionDesc>
                        </SectionHeader>
                        <LogosGrid>
                            <LogoCard>
                                <LogoPreviewBox>
                                    <img src="/brand/luckynote-logo.svg" alt="Luckynote horizontal logo on light background" style={{ maxHeight: '36px' }} />
                                </LogoPreviewBox>
                                <LogoInfo>
                                    <div>
                                        <h3>Horizontal Logo</h3>
                                        <span>Light Background</span>
                                    </div>
                                </LogoInfo>
                                <DownloadRow>
                                    <AssetDownloadLink href="/brand/luckynote-logo.svg" download="luckynote-logo.svg">
                                        <FiDownload size={13} /> SVG
                                    </AssetDownloadLink>
                                    <AssetDownloadLink href="/brand/luckynote-logo.png" download="luckynote-logo.png">
                                        <FiDownload size={13} /> PNG
                                    </AssetDownloadLink>
                                </DownloadRow>
                            </LogoCard>

                            <LogoCard>
                                <LogoPreviewBox $dark>
                                    <img src="/brand/luckynote-logo-white.svg" alt="Luckynote horizontal logo on dark background" style={{ maxHeight: '36px' }} />
                                </LogoPreviewBox>
                                <LogoInfo>
                                    <div>
                                        <h3>Horizontal Logo</h3>
                                        <span>Dark Background</span>
                                    </div>
                                </LogoInfo>
                                <DownloadRow>
                                    <AssetDownloadLink href="/brand/luckynote-logo-white.svg" download="luckynote-logo-white.svg">
                                        <FiDownload size={13} /> SVG
                                    </AssetDownloadLink>
                                    <AssetDownloadLink href="/brand/luckynote-logo-white.png" download="luckynote-logo-white.png">
                                        <FiDownload size={13} /> PNG
                                    </AssetDownloadLink>
                                </DownloadRow>
                            </LogoCard>

                            <LogoCard>
                                <LogoPreviewBox>
                                    <img src={luckyIcon} alt="Luckynote app icon" style={{ maxHeight: '64px' }} />
                                </LogoPreviewBox>
                                <LogoInfo>
                                    <div>
                                        <h3>App Icon Mark</h3>
                                        <span>Standalone Clover</span>
                                    </div>
                                </LogoInfo>
                                <DownloadRow>
                                    <AssetDownloadLink href="/brand/luckynote-icon.svg" download="luckynote-icon.svg">
                                        <FiDownload size={13} /> SVG
                                    </AssetDownloadLink>
                                    <AssetDownloadLink href="/brand/luckynote-icon-512.png" download="luckynote-icon-512.png">
                                        <FiDownload size={13} /> 512px
                                    </AssetDownloadLink>
                                    <AssetDownloadLink href="/brand/luckynote-icon-192.png" download="luckynote-icon-192.png">
                                        <FiDownload size={13} /> 192px
                                    </AssetDownloadLink>
                                </DownloadRow>
                            </LogoCard>

                            <LogoCard>
                                <LogoPreviewBox $blue>
                                    <img src={luckyIcon} alt="Luckynote app icon on brand blue" style={{ maxHeight: '64px' }} />
                                </LogoPreviewBox>
                                <LogoInfo>
                                    <div>
                                        <h3>App Icon Mark</h3>
                                        <span>Brand Surface</span>
                                    </div>
                                </LogoInfo>
                                <DownloadRow>
                                    <AssetDownloadLink href="/brand/luckynote-icon.svg" download="luckynote-icon.svg">
                                        <FiDownload size={13} /> SVG
                                    </AssetDownloadLink>
                                    <AssetDownloadLink href="/brand/luckynote-icon-512.png" download="luckynote-icon-512.png">
                                        <FiDownload size={13} /> PNG
                                    </AssetDownloadLink>
                                </DownloadRow>
                            </LogoCard>
                        </LogosGrid>
                    </Section>

                    {/* Section 3: Colors */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Brand Colors</SectionTitle>
                            <SectionDesc>Click any color swatch to copy its HEX value to your clipboard.</SectionDesc>
                        </SectionHeader>
                        <PaletteGrid>
                            {COLOR_PALETTE.map((color) => (
                                <ColorSwatch key={color.name} onClick={() => handleCopyHex(color.hex)}>
                                    <SwatchPreview $color={color.hex} $border={color.border} />
                                    <SwatchName>{color.name}</SwatchName>
                                    <SwatchValue>
                                        <span>{color.hex}</span>
                                        {copiedColor === color.hex ? (
                                            <FiCheck size={14} color="#34c759" />
                                        ) : (
                                            <FiCopy size={13} />
                                        )}
                                    </SwatchValue>
                                </ColorSwatch>
                            ))}
                        </PaletteGrid>
                    </Section>

                    {/* Section 4: Product Screenshots */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Product Screenshots</SectionTitle>
                            <SectionDesc>High-resolution UI captures of Luckynote in action.</SectionDesc>
                        </SectionHeader>
                        <MediaGrid>
                            <MediaCard>
                                <MediaImageWrap>
                                    <img src={appPreview} alt="Luckynote Web and Desktop App UI" />
                                </MediaImageWrap>
                                <MediaBody>
                                    <div>
                                        <h3>App Interface</h3>
                                        <p>Desktop & Web Workspace</p>
                                    </div>
                                    <AssetDownloadLink href="/brand/luckynote-app-preview.png" download="luckynote-app-preview.png">
                                        <FiDownload size={14} /> Download
                                    </AssetDownloadLink>
                                </MediaBody>
                            </MediaCard>

                            <MediaCard>
                                <MediaImageWrap>
                                    <img src={extensionPreview} alt="Luckynote Chrome and Browser Extension" />
                                </MediaImageWrap>
                                <MediaBody>
                                    <div>
                                        <h3>Browser Extension</h3>
                                        <p>Quick Web Capture & Notes</p>
                                    </div>
                                    <AssetDownloadLink href="/brand/luckynote-extension-preview.png" download="luckynote-extension-preview.png">
                                        <FiDownload size={14} /> Download
                                    </AssetDownloadLink>
                                </MediaBody>
                            </MediaCard>
                        </MediaGrid>
                    </Section>

                    {/* Section 5: Brand Guidelines */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Brand Usage Guidelines</SectionTitle>
                            <SectionDesc>A few friendly rules to keep our brand looking sharp.</SectionDesc>
                        </SectionHeader>
                        <GuidelinesGrid>
                            <GuidelineCard>
                                <h4 style={{ color: '#2e7d32' }}>✓ Do</h4>
                                <ul>
                                    <li>Use the official vector files provided above.</li>
                                    <li>Provide ample clear space around the logo and icon.</li>
                                    <li>Spell "Luckynote" as a single word with a capitalized "L".</li>
                                    <li>Link back to <code>https://luckynote.io</code> when referencing the product.</li>
                                </ul>
                            </GuidelineCard>
                            <GuidelineCard>
                                <h4 style={{ color: '#d32f2f' }}>✕ Don't</h4>
                                <ul>
                                    <li>Do not alter, distort, rotate, or stretch the logo.</li>
                                    <li>Do not recolor the logo with unapproved colors or gradients.</li>
                                    <li>Do not use the Luckynote logo as your own product or app icon.</li>
                                    <li>Do not suggest endorsement or partnership without prior agreement.</li>
                                </ul>
                            </GuidelineCard>
                        </GuidelinesGrid>
                    </Section>

                    {/* Section 6: Press Contact */}
                    <Section id="press-contact">
                        <ContactCard>
                            <h3>Press & Media Inquiries</h3>
                            <p>
                                Writing an article, recording a review, or need custom assets or founder interview quotes? We are happy to help.
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                <PrimaryButton href="mailto:info@luckynote.io?subject=Press%20Inquiry%20-%20Luckynote">
                                    <FiMail size={18} />
                                    info@luckynote.io
                                </PrimaryButton>
                                <SecondaryButton href="/about/uros">
                                    Founder Story & Bio <FiExternalLink size={14} />
                                </SecondaryButton>
                            </div>
                        </ContactCard>
                    </Section>
                </Container>
            </MainContent>

            <Footer />
        </PageWrap>
    );
};

export default Press;
